// Single source of truth for structural (non-i18n, deterministic) widget field
// defaults. Two consumers share this:
//   1. widget-categories.tsx spreads these and layers i18n content/name/options
//      + icons/labels on top for the palette.
//   2. the v6→v7 migration backfills missing fields on stored widgets.
//
// This module must stay free of i18n and React so it can be imported from the
// eager query/migration graph without triggering i18n resolution at load time.
// i18n text fields (content/name) and sample `options` default to empty here;
// the palette overrides them with translated values.

import type { TemplateCheckboxV1 } from '../types/template/checkbox';
import type { TemplateClinicalSignatureV1 } from '../types/template/clinical-signature';
import type { TemplateDatePickerV1 } from '../types/template/date-picker';
import type { TemplateDateRangePickerV1 } from '../types/template/date-range-picker';
import type { TemplateHeadingV1 } from '../types/template/heading';
import type { TemplateImageV1 } from '../types/template/image';
import type { TemplateInputV1 } from '../types/template/input';
import type { TemplateNumberSelectV1 } from '../types/template/number-selector';
import type { TemplatePainScaleV1 } from '../types/template/pain-scale';
import type { TemplateParagraphV1 } from '../types/template/paragraph';
import type { TemplateRadioV1 } from '../types/template/radio';
import type { TemplateSelectV1 } from '../types/template/select';
import type { TemplateSeparatorV1 } from '../types/template/separator';
import type { TemplateSignatureV1 } from '../types/template/signature';
import type { TemplateTextareaV1 } from '../types/template/textarea';
import type { TemplateTimePickerV1 } from '../types/template/time-picker';
import {
  FONT_SIZE_VALUES,
  TEXT_COLOR_VALUES,
  TEXT_FORMAT_VALUES,
  TEXT_TYPE_STYLE,
} from '../types/template/typography';

export const WIDGET_STRUCTURAL_DEFAULTS = {
  heading1: {
    type: 'heading1',
    align: 'left',
    content: '',
    indent: 0,
    color: TEXT_COLOR_VALUES.BLACK,
    textFormat: [TEXT_FORMAT_VALUES.BOLD],
    fontSize: FONT_SIZE_VALUES['5XL'],
  } satisfies Omit<TemplateHeadingV1, 'id'>,
  heading2: {
    type: 'heading2',
    align: 'left',
    content: '',
    indent: 0,
    color: TEXT_COLOR_VALUES.BLACK,
    textFormat: [TEXT_FORMAT_VALUES.BOLD],
    fontSize: FONT_SIZE_VALUES['3XL'],
  } satisfies Omit<TemplateHeadingV1, 'id'>,
  heading3: {
    type: 'heading3',
    align: 'left',
    content: '',
    indent: 0,
    color: TEXT_COLOR_VALUES.BLACK,
    textFormat: [TEXT_FORMAT_VALUES.BOLD],
    fontSize: FONT_SIZE_VALUES['2XL'],
  } satisfies Omit<TemplateHeadingV1, 'id'>,
  paragraph: {
    type: 'paragraph',
    align: 'left',
    content: '',
    indent: 0,
    color: TEXT_COLOR_VALUES.BLACK,
    textFormat: [],
    fontSize: FONT_SIZE_VALUES.BASE,
    textStyle: TEXT_TYPE_STYLE.DEFAULT,
  } satisfies Omit<TemplateParagraphV1, 'id'>,
  separator: {
    type: 'separator',
    style: 'solid',
    color: 'gray',
  } satisfies Omit<TemplateSeparatorV1, 'id'>,
  image: {
    type: 'image',
    name: '',
    showTitle: false,
    uploadedToGallery: false,
  } satisfies Omit<TemplateImageV1, 'id'>,
  signature: {
    type: 'signature',
    name: '',
    showName: true,
    isRequired: false,
    fieldLayout: 'vertical',
    value: '',
  } satisfies Omit<TemplateSignatureV1, 'id'>,
  clinicalSignature: {
    type: 'clinicalSignature',
    name: '',
    showName: true,
    isRequired: false,
    fieldLayout: 'vertical',
    value: '',
  } satisfies Omit<TemplateClinicalSignatureV1, 'id'>,
  inputText: {
    type: 'inputText',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    placeholder: '',
    isRequired: false,
    validationRule: 'none',
    value: '',
    fieldLayout: 'vertical',
    advanceLogics: [],
    showUnit: false,
  } satisfies Omit<TemplateInputV1, 'id'>,
  inputNumber: {
    type: 'inputNumber',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    placeholder: '',
    isRequired: false,
    validationRule: 'none',
    value: '',
    fieldLayout: 'vertical',
    advanceLogics: [],
    showUnit: false,
  } satisfies Omit<TemplateInputV1, 'id'>,
  inputEmail: {
    type: 'inputEmail',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    placeholder: '',
    isRequired: false,
    validationRule: 'none',
    value: '',
    fieldLayout: 'vertical',
    advanceLogics: [],
    showUnit: false,
  } satisfies Omit<TemplateInputV1, 'id'>,
  textarea: {
    type: 'textarea',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    placeholder: '',
    value: '',
    isRequired: false,
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateTextareaV1, 'id'>,
  select: {
    type: 'select',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    options: [],
    isRequired: false,
    isSearchable: false,
    value: '',
    placeholder: '',
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateSelectV1, 'id'>,
  numberSelect: {
    type: 'numberSelect',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    isRequired: false,
    value: '',
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateNumberSelectV1, 'id'>,
  checkbox: {
    type: 'checkbox',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    options: [],
    isRequired: false,
    value: [],
    noteSettings: [],
    optionLayout: 'horizontal',
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateCheckboxV1, 'id'>,
  radio: {
    type: 'radio',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    options: [],
    isRequired: false,
    value: '',
    note: '',
    noteSettings: [],
    optionLayout: 'horizontal',
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateRadioV1, 'id'>,
  datePicker: {
    type: 'datePicker',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    isRequired: false,
    withTime: false,
    value: '',
    defaultNow: false,
    placeholder: '',
    enableLessThanTodayRule: false,
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateDatePickerV1, 'id'>,
  dateRangePicker: {
    type: 'dateRangePicker',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    value: null,
    isRequired: false,
    withTime: false,
    placeholder: '',
    defaultStartDayToday: false,
    defaultNow: false,
    disabledDateRule: 'none',
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateDateRangePickerV1, 'id'>,
  timePicker: {
    type: 'timePicker',
    name: '',
    showName: true,
    labelColor: TEXT_COLOR_VALUES.BLACK,
    value: '',
    isRequired: false,
    withSeconds: false,
    defaultValueType: 'none',
    fieldLayout: 'vertical',
    advanceLogics: [],
  } satisfies Omit<TemplateTimePickerV1, 'id'>,
  painScale: {
    type: 'painScale',
    name: '',
    isRequired: false,
    value: '',
    advanceLogics: [],
    fieldLayout: 'vertical',
  } satisfies Omit<TemplatePainScaleV1, 'id'>,
};
